import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import useModalEscape from "../../hooks/useModalEscape";
import useModalTransition from "../../hooks/useModalTransition";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { notifyError, notifySuccess } from "../../notify";
import { getFriendlyAuthErrorMessage } from "../../authErrors";
import { API_BASE_URL } from "../../config";
import "./LandingPage.css";

import uiScreenshot from "../../ui.png";

// Lucide Icons
import {
  Lock,
  X,
  Mail,
  AlertCircle,
  Eye,
  EyeOff,
  Menu,
  ChevronUp
} from "lucide-react";

import TermsOfServiceModal from "../../components/LegalModals/TermsOfServiceModal";
import PrivacyPolicyModal from "../../components/LegalModals/PrivacyPolicyModal";

// ── Password Reset Modal Component ─────────────────────────────
const PasswordResetModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (isOpen) {
      setEmail("");
      setLoading(false);
    }
  }, [isOpen]);

  const handlePasswordReset = async () => {
    if (!email || !email.trim()) {
      notifyError("Please enter your email address.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      notifyError("Please enter a valid email address.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/auth/send-password-reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email.trim(),
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data.ok) {
        notifySuccess(
          "Password reset email sent! The link will expire in 1 hour."
        );
        onClose();
        setEmail("");
      } else {
        notifyError(
          data.error || "Failed to send reset email. Please try again."
        );
      }
    } catch (error) {
      notifyError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="landing-modal-overlay" onClick={onClose}>
      <div
        className="landing-modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="landing-modal-close-btn" onClick={onClose} aria-label="Close modal">
          <X size={20} />
        </button>

        <div className="password-reset-icon-wrapper">
          <div className="password-reset-icon-circle">
            <Lock size={30} />
          </div>
        </div>

        <h2 className="landing-modal-title">Reset Password</h2>

        <p className="landing-modal-subtitle">
          Enter your email address below and we'll send you a link to reset your
          password. The link will expire in 1 hour.
        </p>

        <div className="landing-input-group">
          <div className="landing-input-icon">
            <Mail size={18} />
          </div>
          <input
            type="email"
            className="landing-text-input"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !loading) {
                handlePasswordReset();
              }
            }}
            autoFocus
            disabled={loading}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <button
            className="landing-modal-submit-btn"
            onClick={handlePasswordReset}
            disabled={loading}
          >
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
        </div>

        <div className="password-reset-footer">
          <p>
            <AlertCircle size={13} style={{ marginRight: "6px", verticalAlign: "middle" }} />
            Make sure to check your spam folder if you don't see the email within a few minutes.
          </p>
        </div>
      </div>
    </div>
  );
};

// ── About Avisa Modal Component ────────────────────────────────
const AboutModal = ({ isOpen, onClose }) => {
  useModalEscape(onClose, isOpen);
  const shouldRender = useModalTransition(isOpen);
  if (!shouldRender) return null;

  return (
    <div
      className={`landing-modal-overlay modal-transition-${isOpen ? "enter" : "exit"}`}
      onClick={onClose}
    >
      <div className="legal-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="landing-modal-close-btn" onClick={onClose} aria-label="Close modal">
          <X size={20} />
        </button>

        <div className="legal-modal-header">
          <h2 className="legal-modal-title">About Avisa</h2>
          <p className="legal-modal-subtitle">Our mission and legislative technology</p>
        </div>

        <div className="legal-modal-body">
          <p>
            Avisa is an AI-Enhanced Legal Information Management System designed
            to bridge the legal literacy gap in the Philippines by making national
            laws and local ordinances accessible to every Filipino citizen.
            Developed as a capstone project by students from National University,
            the platform transforms complex legal documents into plain-language
            summaries and Filipino translations using Google's Gemini AI, while
            preserving legal accuracy through expert verification.
          </p>
          <p>
            The system features jurisdiction-aware intelligence that prioritizes
            relevant barangay and municipal ordinances, a RAG-powered chatbot
            answering legal questions with cited sources, and direct access to
            original scanned documents for verification. Built with a three-tier
            cloud-native architecture using React Native, React.js, and Firebase,
            Avisa serves both citizens seeking to understand their rights and
            Local Government Units needing efficient ordinance management tools.
          </p>
          <div className="legal-modal-footer-note">
            © 2026 Avisa. All rights reserved. Capstone Project — National University.
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Scope & Limitations Modal Component ────────────────────────
const ScopeLimitationsModal = ({ isOpen, onClose }) => {
  useModalEscape(onClose, isOpen);
  const shouldRender = useModalTransition(isOpen);
  if (!shouldRender) return null;

  return (
    <div
      className={`landing-modal-overlay modal-transition-${isOpen ? "enter" : "exit"}`}
      onClick={onClose}
    >
      <div className="legal-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="landing-modal-close-btn" onClick={onClose} aria-label="Close modal">
          <X size={20} />
        </button>

        <div className="legal-modal-header">
          <h2 className="legal-modal-title">Scope &amp; Limitations</h2>
          <p className="legal-modal-subtitle">Project coverage and defined boundaries</p>
        </div>

        <div className="legal-modal-body">
          <section className="legal-scope-section">
            <h3 className="legal-scope-heading">Scope of the Project</h3>
            <p>
              This study focuses on the design, development, and technical validation
              of Avisa, an AI-integrated legal information system consisting of a
              cross-platform mobile application for citizens and a web-based
              administrative control center for local officials, built on a
              Three-Tier Cloud-Native Architecture.
            </p>
            <p>
              Avisa uses Gemini 3.1 Flash Lite to transform unstructured legal documents
              into structured, bilingual English and Filipino data. The pilot covers
              national mother laws connected to local ordinances within one selected LGU
              and three pilot barangays for hyper-local ordinance mapping.
            </p>
            <p>
              The system includes Context-Augmented Generation chat, role-based access
              control, real-time monitoring of national laws, and expert validation of
              AI-generated summaries.
            </p>
          </section>

          <section className="legal-scope-section">
            <h3 className="legal-scope-heading">Delimitations and Limitations</h3>
            <p>
              Manual data gathering and encoding are limited to one selected LGU,
              three selected pilot barangays within that municipality, and the mother
              laws connected to their local ordinances:
            </p>
            <ul>
              <li>Manual gathering for non-pilot barangays is outside this phase.</li>
              <li>Avisa is strictly an informational tool and does not constitute formal legal advice.</li>
              <li>Offline AI operation is not supported as Gemini API and Firebase Cloud require connectivity.</li>
              <li>Public access to this administrative web application is restricted to authorized administrative personnel.</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

// ── Scroll to Top Button (Matching PublicLandingPage) ──────────
const ScrollToTopButton = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShow(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          style={{
            position: "fixed",
            bottom: "32px",
            right: "32px",
            padding: "12px 20px",
            borderRadius: "24px",
            background: "#0f172a",
            color: "white",
            border: "none",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            zIndex: 100,
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          <ChevronUp size={16} />
          Go to Top
        </motion.button>
      )}
    </AnimatePresence>
  );
};

// ── Main LandingPage Component ─────────────────────────────────
const LandingPage = () => {
  useDocumentTitle("Avisa Admin Dashboard");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isScopeOpen, setIsScopeOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { login, logout, user, loading, isAdmin, isSystemAdmin } = useAuth();
  const isElectronBuild = process.env.REACT_APP_ELECTRON === "true";
  const adminDesktopDownloadUrl = process.env.REACT_APP_ADMIN_DESKTOP_DOWNLOAD_URL || "#";
  const avisaLogo = `${process.env.PUBLIC_URL}/images/avisa.svg`;

  const startDesktopDownload = useCallback(() => {
    window.location.href = adminDesktopDownloadUrl;
  }, [adminDesktopDownloadUrl]);

  const handleDesktopDownload = () => {
    startDesktopDownload();
  };

  const trackFailedLogin = async (loginEmail) => {
    try {
      let clientIp = "unknown";
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        const data = await response.json();
        clientIp = data.ip;
      } catch (err) {
        clientIp = "unknown";
      }

      await fetch(`${API_BASE_URL}/api/security/track-failed-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: loginEmail,
          ipAddress: clientIp,
          userAgent: navigator.userAgent,
          isAdminLogin: true,
        }),
      });
    } catch (error) {
      console.error("Failed to track login attempt:", error);
    }
  };

  const redirectBasedOnRole = useCallback(() => {
    if (isSystemAdmin()) {
      navigate("/system-admin/dashboard");
    } else if (isAdmin()) {
      navigate("/dashboard");
    } else if (user) {
      notifyError("You do not have access to this portal.");
      logout();
    }
  }, [isSystemAdmin, isAdmin, navigate, logout, user]);

  useEffect(() => {
    if (!loading && user) {
      redirectBasedOnRole();
    }
  }, [user, loading, redirectBasedOnRole]);

  const closeAllModals = () => {
    setIsModalOpen(false);
    setIsPasswordResetModalOpen(false);
    setIsAboutOpen(false);
    setIsScopeOpen(false);
    setIsTermsOpen(false);
    setIsPrivacyOpen(false);
    setIsMobileMenuOpen(false);
  };

  useModalEscape(
    closeAllModals,
    isModalOpen ||
      isPasswordResetModalOpen ||
      isAboutOpen ||
      isScopeOpen ||
      isTermsOpen ||
      isPrivacyOpen ||
      isMobileMenuOpen
  );

  const openModal = () => {
    setIsModalOpen(true);
    setEmail("");
    setPassword("");
    setIsMobileMenuOpen(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEmail("");
    setPassword("");
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === "modal-overlay") {
      closeModal();
    }
  };

  const openPasswordResetModal = () => {
    closeModal();
    setIsPasswordResetModalOpen(true);
  };

  const closePasswordResetModal = () => {
    setIsPasswordResetModalOpen(false);
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    openPasswordResetModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);

    if (!email || !password) {
      notifyError("Please enter both email and password");
      setIsLoggingIn(false);
      return;
    }

    try {
      const result = await login(email, password);
      closeModal();

      if (result?.role === "system_admin") {
        navigate("/system-admin/dashboard");
      } else if (result?.role === "admin") {
        navigate("/dashboard");
      } else {
        notifyError("You do not have access to this portal.");
        await logout();
        return;
      }
    } catch (error) {
      console.error("Login error:", error);
      await trackFailedLogin(email);
      notifyError(getFriendlyAuthErrorMessage(error));
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="landing-loading-screen">
        <div className="landing-loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="landing-page-root">
      {/* ── Top Navigation Bar ──────────────────────── */}
      <motion.header
        className={`landing-navbar ${isMobileMenuOpen ? "landing-navbar-mobile-open" : ""}`}
        id="landing-navbar"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="landing-navbar-container">
          {/* Brand Logo */}
          <img
            src={avisaLogo}
            alt="Avisa"
            className="landing-brand-logo"
            id="landing-logo"
          />

          {/* Navigation Links with Admin Login Button (Matching Public Landing Page) */}
          <nav className="landing-nav" aria-label="Main navigation">
            <button
              type="button"
              className="landing-nav-link"
              onClick={() => setIsAboutOpen(true)}
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              About
            </button>
            <button
              type="button"
              className="landing-nav-link"
              onClick={() => setIsScopeOpen(true)}
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              Scope &amp; Limitations
            </button>
            <button
              type="button"
              className="landing-nav-link"
              onClick={() => setIsTermsOpen(true)}
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              Terms of Service
            </button>
            <button
              type="button"
              className="landing-nav-link"
              onClick={() => setIsPrivacyOpen(true)}
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              Privacy Policy
            </button>
            <button
              type="button"
              onClick={openModal}
              className="landing-btn landing-btn-primary"
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                marginLeft: "16px",
              }}
            >
              Admin Login
            </button>
          </nav>

          {/* Mobile Hamburger Button */}
          <button
            type="button"
            className="landing-mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span className={`landing-hamburger ${isMobileMenuOpen ? "open" : ""}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>

          {/* Mobile Navigation Overlay */}
          <div className={`landing-mobile-nav ${isMobileMenuOpen ? "open" : ""}`}>
            <button
              type="button"
              className="landing-mobile-nav-link"
              onClick={() => {
                setIsAboutOpen(true);
                setIsMobileMenuOpen(false);
              }}
            >
              About
            </button>
            <button
              type="button"
              className="landing-mobile-nav-link"
              onClick={() => {
                setIsScopeOpen(true);
                setIsMobileMenuOpen(false);
              }}
            >
              Scope &amp; Limitations
            </button>
            <button
              type="button"
              className="landing-mobile-nav-link"
              onClick={() => {
                setIsTermsOpen(true);
                setIsMobileMenuOpen(false);
              }}
            >
              Terms of Service
            </button>
            <button
              type="button"
              className="landing-mobile-nav-link"
              onClick={() => {
                setIsPrivacyOpen(true);
                setIsMobileMenuOpen(false);
              }}
            >
              Privacy Policy
            </button>
            <div style={{ padding: "16px", marginTop: "8px", display: "flex", flexDirection: "column", gap: "12px" }}>
              <button
                type="button"
                className="landing-btn landing-btn-primary"
                style={{
                  width: "100%",
                  justifyContent: "center",
                  padding: "12px",
                }}
                onClick={() => {
                  openModal();
                  setIsMobileMenuOpen(false);
                }}
              >
                Admin Login
              </button>
              {!isElectronBuild && (
                <button
                  type="button"
                  className="landing-mobile-nav-btn landing-mobile-nav-btn-outline"
                  onClick={() => {
                    handleDesktopDownload();
                    setIsMobileMenuOpen(false);
                  }}
                >
                  Download Desktop App
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.header>

      {/* ── Hero Section (matching typography & CTAs of ui.png) ── */}
      <section className="landing-hero-section">
        <div className="landing-hero-content">
          <motion.h1
            className="landing-hero-headline"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <span className="hero-accent-text">
              Governance Starts with Clarity
            </span>
            <span className="hero-white-text">
              Centralized legislative oversight in the Philippines.
            </span>
          </motion.h1>

          <motion.p
            className="landing-hero-subtitle"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Streamline administrative operations and legislative records on one secure,
            centralized platform — built to elevate governance and oversight across Philippine
            Local Government Units.
          </motion.p>

          <motion.div
            className="landing-hero-cta-group"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <button
              type="button"
              className="landing-hero-btn landing-hero-btn-primary"
              onClick={openModal}
            >
              Admin Login
            </button>

            {!isElectronBuild && (
              <button
                type="button"
                className="landing-hero-btn landing-hero-btn-outline"
                onClick={handleDesktopDownload}
              >
                Download Desktop App
              </button>
            )}
          </motion.div>
        </div>

        {/* ── Bottom Dashboard Preview (Screenshot ui.png) ───────── */}
        <motion.div
          className="landing-dashboard-preview-container"
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.3 }}
        >
          <div className="landing-dashboard-mockup">
            <img
              src={uiScreenshot}
              alt="Avisa Platform Overview Dashboard"
              className="landing-dashboard-screenshot"
            />
          </div>
        </motion.div>
      </section>

      {/* ── Footer (Similar to Public Landing Page, with #e8e8e8 background) ── */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-main">
            <div className="landing-footer-brand">
              <img
                src={avisaLogo}
                alt="Avisa"
                className="landing-footer-logo"
              />
              <p>
                Making Philippine legal information easier to find, understand,
                and use for every Filipino.
              </p>
            </div>

            <div className="landing-footer-links" aria-label="Footer navigation">
              <div className="landing-footer-column">
                <h3>Explore</h3>
                <button type="button" onClick={() => setIsAboutOpen(true)}>
                  About Avisa
                </button>
                <button type="button" onClick={() => setIsScopeOpen(true)}>
                  Scope &amp; Limitations
                </button>
              </div>

              <div className="landing-footer-column">
                <h3>Platform</h3>
                {!isElectronBuild && (
                  <button type="button" onClick={handleDesktopDownload}>
                    Download Desktop App
                  </button>
                )}
                <button type="button" onClick={openModal}>
                  Administrative Portal
                </button>
              </div>

              <div className="landing-footer-column">
                <h3>Legal</h3>
                <button type="button" onClick={() => setIsTermsOpen(true)}>
                  Terms of Service
                </button>
                <button type="button" onClick={() => setIsPrivacyOpen(true)}>
                  Privacy Policy
                </button>
              </div>
            </div>
          </div>

          <div className="landing-footer-bottom">
            <p className="landing-footer-copy">
              &copy; {new Date().getFullYear()} Avisa. All rights reserved.
            </p>
            <p className="landing-footer-note">Justice Starts with Understanding</p>
          </div>
        </div>
      </footer>

      {/* ── Admin Login Modal (Original component restored) ─────── */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleOverlayClick}>
          <div className="modal-content">
            <h2 className="modal-title">Admin Login</h2>
            <p className="modal-subtitle">
              Enter your credentials to access the control center
            </p>

            <form className="modal-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <div className="input-icon-wrapper">
                  <input
                    type="email"
                    id="email"
                    placeholder="hello@example.com"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoggingIn}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-icon-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="**********"
                    className="form-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoggingIn}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="form-options">
                <label className="checkbox-label">
                  <input type="checkbox" disabled={isLoggingIn} /> Keep me
                  signed in
                </label>
                <a
                  href="#forgot"
                  className="forgot-link"
                  onClick={handleForgotPasswordClick}
                >
                  Forgot Password?
                </a>
              </div>

              <button
                type="submit"
                className="modal-login-btn"
                disabled={isLoggingIn}
              >
                {isLoggingIn ? "Logging in..." : "Admin Login"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Password Reset Modal ────────────────────────────────── */}
      <PasswordResetModal
        isOpen={isPasswordResetModalOpen}
        onClose={closePasswordResetModal}
      />

      {/* ── Legal & Info Modals (About, Scope, Terms, Privacy) ─── */}
      <AboutModal isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
      <ScopeLimitationsModal isOpen={isScopeOpen} onClose={() => setIsScopeOpen(false)} />
      <TermsOfServiceModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
      <PrivacyPolicyModal isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />

      {/* ── Scroll to Top Button (Matching PublicLandingPage) ── */}
      <ScrollToTopButton />
    </div>
  );
};

export default LandingPage;
